import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiResponse } from 'src/app/shared/models/api-response.interface';
import { CancelData } from 'src/app/shared/models/cancel-data.model';
import { HttpService } from './http.service';
import { ContractCancellationInfo } from '../../../shared/models/contract-cancellation-info.interface';
import {
  CancellationAdditionalQuestionsPayload,
  CancellationData,
  CancellationReportPayloadItem,
  CreateCancellationCommissionBody,
  UpdateCancellationFieldsPayload,
} from 'src/app/modules/contracts/models/cancellation-data.model';
import { HttpHeaders } from '@angular/common/http';
import { CancelCalculation } from 'src/app/shared/models/cancel-calculations.model';
import { API_VERSIONS } from 'src/app/shared/consts/api-versions.const';
import { CancellationAuditLog } from 'src/app/modules/contracts/components/contract-details/contract-details-cancellation/cancellation-audit-log/cancellation-audit-log.component';
import { ContractAuditLog } from 'src/app/shared/models/contract-audit-log.model';
import {
  ContractBulkStatusUpdateBody,
  ContractPartialUpdateBody,
  ContractStatusUpdateBody,
} from 'src/app/shared/models/contract.interface';
import { ContractTransaction, CreateContractTransactionBody } from '../../models/contracts/contract-transaction.model';
import { TransactionPayment, GetTransactionPaymentParams, CreateTransactionPaymentBody } from '../../models/contracts/contract-transaction-payment.model';
import {
  AllocationActivityItem,
  AllocationActivityParams,
} from 'src/app/shared/models/allocation-activity.model';

@Injectable({
  providedIn: 'root',
})
export class ContractsApiService {
  constructor(private apiService: HttpService) {}

  getAll(params: any): Observable<ApiResponse> {
    return this.apiService.getReq(
      `/${API_VERSIONS.CONTRACTS}/contracts/paging`,
      { params }
    );
  }

  getById(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contracts/${id}`);
  }

  previewContractDocument(id: number, templateId?: number): Observable<any> {
    return this.apiService.getReq(`/contracts/${id}/contract-document/preview`, {
      responseType: 'blob',
      observe: 'response',
      params: templateId ? { template_id: templateId } : {},
    });
  }

  getContractDocumentTemplates(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contracts/${id}/contract-document/templates`);
  }

  downloadContractDocument(id: number, templateId?: number): Observable<any> {
    return this.apiService.getReq(`/contracts/${id}/contract-document/download`, {
      ...(templateId ? { params: { template_id: templateId } } : {}),
      responseType: 'blob',
      observe: 'response',
    });
  }

  emailContractDocument(id: number, body: any): Observable<ApiResponse> {
    return this.apiService.postReq(`/contracts/${id}/contract-document/email`, body);
  }

  getContractDocumentEmails(id: number, page = 1): Observable<ApiResponse> {
    return this.apiService.getReq(`/contracts/${id}/contract-document/emails`, { params: { per_page: 15, page } });
  }

  deleteContract(id: number): Observable<ApiResponse> {
    return this.apiService.deleteReq(`/contracts/${id}`);
  }

  voidContract(contractId: number): Observable<ApiResponse> {
    return this.apiService.putReq(`/contracts/${contractId}/void`);
  }

  bulkVoidContracts(contractIds: number[]): Observable<ApiResponse> {
    return this.apiService.putReq('/contracts/void/bulk', {
      contract_ids: contractIds,
    });
  }

  calculateCancellation(
    cancelData: CancelData,
    headers?: HttpHeaders
  ): Observable<ApiResponse<CancelCalculation>> {
    return this.apiService.postReq('/cancellation/calculations', cancelData, {
      headers,
    });
  }

  sendCancelQuote(
    id: number,
    cancelRequestData: any,
    headers?: HttpHeaders
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contracts/${id}/cancel`,
      cancelRequestData,
      { headers }
    );
  }

  getQuoteByVin(vin: string): Observable<ApiResponse> {
    return this.apiService.getReq(`/vin/available-quotes/${vin}`);
  }

  approveCancellation(
    id: number,
    approveToken: string
  ): Observable<ApiResponse> {
    return this.apiService.getReq(
      `/cancellation/${id}/clientApprove/${approveToken}`
    );
  }

  approveAdminCancellation(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contractCancellation/${id}/approve`);
  }

  sendCancellationApprovalEmail(
    id: number,
    emails: string[] = []
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contractCancellation/${id}/send-approval-email`,
      { emails }
    );
  }

  requestCancellationDocumentReupload(
    id: number,
    payload: { emails?: string[]; message?: string }
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contractCancellation/${id}/request-document-reupload`,
      payload
    );
  }

  changeCancellationStatus(
    cancellationId: number,
    status: 'requested' | 'in_progress' | 'approved' | 'void' | 'paid' | 'archived'
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/${API_VERSIONS.CONTRACT_CANCELLATIONS}/contract_cancellation/main-status/${cancellationId}`,
      { status }
    );
  }

  unCancelContract(id: number): Observable<ApiResponse> {
    return this.apiService.putReq(`/contracts/${id}/unCancel`);
  }

  getTemplates(): Observable<ApiResponse> {
    return this.apiService.getReq('/template');
  }

  getContractsClaims(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contracts/claims-tab/${id}`);
  }

  getCancellationData(
    cancellationId: number
  ): Observable<ApiResponse<CancellationData>> {
    return this.apiService.getReq(
      `/${API_VERSIONS.CONTRACT_CANCELLATIONS}/contract_cancellation/${cancellationId}`
    );
  }

  getCancellationsList(
    contractId: number
  ): Observable<ApiResponse<ContractCancellationInfo>> {
    return this.apiService.getReq(
      `/contracts/${contractId}/contract-cancellation-calculations`
    );
  }

  uploadCancellationAttachment(
    id: number,
    file: File
  ): Observable<ApiResponse> {
    let formData = new FormData();
    formData.append('attachement', file);
    formData.append('model', 'ContractCancellation');
    formData.append('model_id', id + '');
    return this.apiService.postReq('/files', formData);
  }

  createCancellaionNote(id: number, content: string): Observable<ApiResponse> {
    const payload = {
      content,
      model: 'ContractCancellation',
      model_id: id,
      is_public: 1,
    };
    return this.apiService.postReq('/notes', payload);
  }

  voidCancellation(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contract_cancellation/${id}/to-void`);
  }

  createThirdPartiesCommissions(
    id: number,
    payload: CreateCancellationCommissionBody
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contract-cancellation-commission/${id}`,
      payload
    );
  }

  deleteCancellationCommission(
    cancellationId: number,
    commissionId: number
  ): Observable<ApiResponse> {
    return this.apiService.deleteReq(
      `/contract-cancellation-commission/${cancellationId}/${commissionId}`
    );
  }

  getCancellationLogs(id: number): Observable<CancellationAuditLog[]> {
    return this.apiService
      .getReq(`/contract_cancellation/${id}/logs`)
      .pipe(map((res) => res.data));
  }

  generateCancellationReport(
    cancellationId: number,
    payload: CancellationReportPayloadItem[]
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contract_cancellation/${cancellationId}/generate-cancellation-report`,
      { report_data: payload }
    );
  }

  updateAdditionalQuestions(
    cancellationId: number,
    payload: CancellationAdditionalQuestionsPayload
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contract_cancellation/${cancellationId}/update-addition-questions`,
      payload
    );
  }

  updateCancellationReason(
    cancellationId: number,
    reason_id: number
  ): Observable<ApiResponse> {
    return this.apiService.putReq(
      `/contract_cancellation/${cancellationId}/update-cancellation-reason`,
      { reason_id }
    );
  }

  updateCancellationFields(
    cancellationId: number,
    body: UpdateCancellationFieldsPayload
  ): Observable<ApiResponse> {
    return this.apiService.putReq(
      `/contract-cancellation/${cancellationId}`,
      body
    );
  }

  loadQuote(id: string): Observable<ApiResponse> {
    return this.apiService.getReq(`/cancellation/quote/${id}`);
  }

  createManualCancellation(payload: unknown): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contract-cancellations/manual-cancellation`,
      payload
    );
  }

  getAuditLogs(contractId: number): Observable<ContractAuditLog[]> {
    return this.apiService
      .postReq('/model-activity-and-notes', {
        model_type: 'contract',
        model_id: contractId,
      })
      .pipe(map((response) => response.data));
  }

  reassignCancellationUser(
    cancellationId: number,
    userId: number
  ): Observable<ApiResponse> {
    return this.apiService.putReq(
      `/contracts/contract_cancellation/${cancellationId}/users/${userId}/reassign-user`
    );
  }

  updateContractPartially(
    contractId: number,
    body: ContractPartialUpdateBody
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contracts/${contractId}/partial-update`,
      body
    );
  }

  getAccountingDetails(contractId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contracts/${contractId}/accounting`);
  }

  getContractTransactions(
    contractId: number,
    params: any
  ): Observable<ApiResponse<ContractTransaction[]>> {
    return this.apiService.getReq(`/contracts/${contractId}/transactions`, {
      params,
    });
  }

  createContractTransaction(
    contractId: number,
    payload: CreateContractTransactionBody
  ): Observable<ApiResponse<ContractTransaction>> {
    return this.apiService.postReq(`/contracts/${contractId}/transactions`, payload);
  }

  updateContractTransaction(
    contractId: number,
    transactionId: number,
    payload: CreateContractTransactionBody
  ): Observable<ApiResponse<ContractTransaction>> {
    return this.apiService.putReq(
      `/contracts/${contractId}/transactions/${transactionId}`,
      payload
    );
  }

  deleteContractTransaction(
    transactionId: number
  ): Observable<ApiResponse<null>> {
    return this.apiService.deleteReq(`/contracts/transactions/${transactionId}`);
  }

  getContractTransactionPayments(
    params: GetTransactionPaymentParams = {}
  ): Observable<TransactionPayment[]> {
    return this.apiService
      .getReq(`/contract-transactions-payments`, { params })
      .pipe(map((response) => response.data));
  }

  createContractTransactionPayment(
    body: CreateTransactionPaymentBody
  ): Observable<ApiResponse<TransactionPayment>> {
    return this.apiService.postReq(`/contract-transactions-payments`, body);
  }

  updateContractTransactionPayment(
    paymentId: number,
    body: CreateTransactionPaymentBody
  ): Observable<ApiResponse<TransactionPayment>> {
    return this.apiService.putReq(
      `/contract-transactions-payments/${paymentId}`,
      body
    );
  }

  deleteContractTransactionPayment(
    paymentId: number
  ): Observable<ApiResponse<TransactionPayment[]>> {
    return this.apiService.deleteReq(`/contract-transactions-payments/${paymentId}`);
  }

  reactivateContract(
    contractId: number,
    payload: { date: string; notes: string; miles?: number | null; status: string }
  ): Observable<ApiResponse> {
    return this.apiService.postReq(`/contracts/${contractId}/reactivate`, payload);
  }

  updateContractStatus(
    contractId: number,
    body: ContractStatusUpdateBody
  ): Observable<ApiResponse> {
    return this.apiService.putReq(`/contracts/${contractId}/status`, body);
  }

  bulkUpdateContractStatus(
    body: ContractBulkStatusUpdateBody
  ): Observable<ApiResponse> {
    return this.apiService.putReq('/contracts/status/bulk', body);
  }

  /**
   * Get list of active payment methods for contract remit
   */
  getActivePaymentMethods(): Observable<ApiResponse> {
    return this.apiService.getReq('/list-payment-methods');
  }

  /**
   * Calculate contract remit - preview statistics and summary for selected contracts
   * @param payload Object containing contract_ids array
   */
  calculateContractRemit(payload: {
    contract_ids: number[];
  }): Observable<ApiResponse> {
    return this.apiService.postReq('/contracts/calculate-contract-remit', payload);
  }

  /**
   * Confirm and process contract remit
   * @param payload Object containing contract_ids array and payment details
   */
  remitContracts(payload: {
    contract_ids: number[];
    payment_method_id: number;
    user_payment_method_id?: number;
    card_number?: string;
    expiry_date?: string;
    cvc?: string;
    zip_code?: string;
    check_number?: string;
    batch_number?: string;
    source?: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    phone?: string;
    account_holder_name?: string;
    bank_name?: string;
    account_type?: string;
    account_number?: string;
    routing_number?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
  }): Observable<ApiResponse> {
    return this.apiService.postReq('/contracts/remit-contracts', payload);
  }

  getMyPaymentMethods(): Observable<ApiResponse> {
    return this.apiService.getReq('/my-payment-methods');
  }

  createUserPaymentMethod(payload: {
    account_holder_name: string;
    bank_name: string;
    account_type: string;
    account_number: string;
    routing_number: string;
  }): Observable<ApiResponse> {
    return this.apiService.postReq('/user-payment-methods', payload);
  }

  deleteUserPaymentMethod(id: number | string): Observable<ApiResponse> {
    return this.apiService.deleteReq(`/user-payment-methods/${id}`);
  }

  /**
   * Get allocated funding transactions for a contract
   * @param contractId The contract ID
   */
  getAllocatedFundingTransactions(contractId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contracts/${contractId}/allocated-funding-transactions`);
  }

  /**
   * Approve contract allocated funding transactions
   * @param contractId The contract ID
   * @param payload Object containing approved_transactions array with payment details
   */
  approveContractAllocatedFundingTransactions(
    contractId: number,
    payload: {
      approved_transactions: Array<{
        transaction_id: number;
        payment_date: string;
        check_number?: string;
        has_existing_payment: boolean;
      }>;
    }
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contracts/${contractId}/approve-contract-allocated-funding-transactions`,
      payload
    );
  }

  /**
   * Deny contract allocated funding transactions
   * @param contractId The contract ID
   * @param payload Object containing denial_reason and denied_transactions array
   */
  denyContractAllocatedFundingTransactions(
    contractId: number,
    payload: {
      denial_reason: string;
      denied_transactions: Array<{
        transaction_id: number;
        batch_number: string;
        amount: number;
      }>;
    }
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contracts/${contractId}/deny-contract-allocated-funding-transactions`,
      payload
    );
  }

  bulkApproveAllocatedFundingTransactions(payload: {
    approved_transactions: Array<{
      contract_id: number;
      payment_date: string;
      check_number: string | null;
    }>;
  }): Observable<ApiResponse> {
    return this.apiService.postReq(
      '/contracts/bulk-approve-allocated-funding-transactions',
      payload
    );
  }

  bulkDenyAllocatedFundingTransactions(payload: {
    contract_ids: number[];
    denial_reason: string;
  }): Observable<ApiResponse> {
    return this.apiService.postReq(
      '/contracts/bulk-deny-allocated-funding-transactions',
      payload
    );
  }

  getRemitBatch(remitBatchId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`/contracts/remit-batches/${remitBatchId}`);
  }

  approveRemitBatch(
    remitBatchId: number,
    payload: { payment_date: string; check_number?: string | null }
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contracts/approve-remit-batch/${remitBatchId}`,
      payload
    );
  }

  rejectRemitBatch(
    remitBatchId: number,
    formData: FormData
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `/contracts/reject-remit-batch/${remitBatchId}`,
      formData
    );
  }

  submitRemitBatch(payload: {
    reference_number: string;
    source: string;
    check_number?: string;
    contract_ids: number[];
  }): Observable<ApiResponse> {
    return this.apiService.postReq('/contracts/submit-remit-batch', payload);
  }

  importRemitBatch(formData: FormData): Observable<ApiResponse> {
    return this.apiService.postReq('/contracts/import-remit-batch', formData);
  }

  getAllocationActivity(
    contractId: number,
    params: AllocationActivityParams = {}
  ): Observable<ApiResponse<AllocationActivityItem[]>> {
    return this.apiService.getReq(
      `/${API_VERSIONS.CONTRACTS}/contracts/${contractId}/allocation-activity`,
      { params }
    );
  }
}
